import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CircularProgress,
  Typography,
  Grid,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageContainer from "../component/PageContainer";
import { Helmet } from "react-helmet-async";
import Pdf from "../assets/icon/pdf.svg";
import CustomPagination from "../component/Pagination";

const JournalDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { journalTitle: initialTitle, journalDescription: initialDescription } =
    location.state || {};

  console.log("Location State (initial):", initialTitle, initialDescription);

  const [journal, setJournal] = useState({
    title: initialTitle || "",
    description: initialDescription || "",
    pdf: "",
    written_by: "",
    articles: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedData, setFetchedData] = useState(false);

  useEffect(() => {
    const fetchJournalDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `https://cms.nti.edu.ng/api/journal/${id}`
        );
        console.log("Journal Details Data:", response.data);

        if (
          response.data &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const foundJournal = response.data.data.find(
            (item) => String(item.id) === String(id)
          );

          if (foundJournal) {
            setJournal({
              title: foundJournal.title,
              description: foundJournal.description,
              pdf: foundJournal.pdf, // Assuming top-level PDF if available for journal
              written_by: foundJournal.written_by, // Assuming top-level written_by if available
              articles: foundJournal.articles || [],
            });
            setFetchedData(true);
          } else {
            setError("Journal with this ID not found in the response.");
          }
        } else {
          setError("Journal not found or data is empty.");
        }
      } catch (err) {
        console.error("Error fetching journal details:", err);
        if (err.response && err.response.status === 404) {
          setError("Journal not found.");
        } else {
          setError(
            "Failed to load journal details. Please check your internet connection or server status."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchJournalDetails();
    } else {
      setLoading(false);
      setError("No journal ID provided in the URL.");
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const BackButton = () => (
    <Box sx={{ mb: 2 }}>
      <IconButton aria-label="back" onClick={handleBack} sx={{ color: "#000" }}>
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );

   
  if (loading) {
    if (initialTitle || initialDescription) {
      return (
        <PageContainer>
          <Helmet>
            <title>
              {initialTitle || "Loading Journal"} | Journals | National
              Teachers' Institute Kaduna
            </title>
          </Helmet>
          <div className="journal-details-wrapper">
            <BackButton />
            <Typography variant="h4" component="h1" gutterBottom>
              {initialTitle}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {initialDescription}
            </Typography>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: "100px" }}
            >
              <CircularProgress sx={{ color: "#46C35F" }} size={24} />
              <Typography variant="body2" style={{ marginLeft: "10px" }}>
                Loading full details...
              </Typography>
            </Grid>
          </div>
        </PageContainer>
      );
    }

    return (
      <PageContainer>
        <BackButton />
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "200px" }}
        >
          <CircularProgress sx={{ color: "#46C35F" }} />
          <Typography variant="body1" style={{ marginLeft: "10px" }}>
            Loading Journal Details...
          </Typography>
        </Grid>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <BackButton />
        <Typography
          variant="h6"
          color="error"
          style={{ textAlign: "center", marginTop: "50px" }}
        >
          {error}
        </Typography>
      </PageContainer>
    );
  }

  if (!journal || !journal.title) {
    return (
      <PageContainer>
        <BackButton />
        <Typography
          variant="h6"
          style={{ textAlign: "center", marginTop: "50px" }}
        >
          Journal not found.
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Helmet>
        <title>
          {journal.title} | Journals | National Teachers' Institute Kaduna
        </title>
      </Helmet>
      <div className="journal-details-wrapper">
        <BackButton />
        <p style={{ fontSize: "22px", fontWeight: "700" }}>
          {journal.title || "Journal Title Not Available"}
        </p>
        <p
          style={{
            fontSize: "14px",
            fontWeight: "500",
            color: "#2E2E2E",
            lineHeight: "30px",
            marginTop: "20px",
          }}
        >
          {journal.description || "No description available."}
        </p>

        <div className="article-header">
          <p style={{ color: "#2E2E2E" }}>Articles</p>
          <hr color="#2E2E2E" style={{ width: "100%" }} />
        </div>

        {journal.articles && journal.articles.length > 0 ? (
          journal.articles.map((article) => (
            <Box key={article.id} sx={{ mb: 3, mt: 10 }}>
              <p style={{ fontSize: "22px", color: "#2E2E2E" }}>
                {article.title}
              </p>
              {article.written_by && (
                <p
                  style={{
                    fontSize: "14px",
                    color: "#2E2E2E",
                    fontWeight: "400",
                  }}
                >
                  <strong></strong> {article.written_by}
                </p>
              )}
              {article.pdf && (
                <div
                  style={{
                    borderStyle: "solid",
                    borderColor: "#e3e3e3",
                    padding: 5,
                    borderWidth: 1,
                    width: 40,
                  }}
                >
                  <a
                    href={article.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={Pdf}
                      alt={`View PDF for ${article.title}`}
                      className="journal-image"
                      style={{
                        maxWidth: "30px",
                        height: "auto",
                        borderRadius: "8px",
                      }}
                    />
                  </a>
                </div>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No articles found for this journal.
          </Typography>
        )}

        {journal.pdf && !journal.articles.length && (
          <Button
            variant="contained"
            href={journal.pdf}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 2,
              backgroundColor: "#46C35F",
              "&:hover": { backgroundColor: "#3a9e4b" },
            }}
          >
            View Journal PDF
          </Button>
        )}
      </div>

       {/* <Grid item xs={12}>
                  <CustomPagination
                    count={journals.last_page}
                    page={journals.current_page - 1}
                    onPageChange={handleChangePage}
                    rowsPerPage={journals.per_page}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    startIndex={startIndex}
                    endIndex={endIndex}
                  />
                </Grid> */}
    </PageContainer>
  );
};

export default JournalDetails;
